import {
  applyActionCode,
  Auth,
  browserLocalPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  getMultiFactorResolver,
  GoogleAuthProvider,
  multiFactor,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  OAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  reauthenticateWithPopup,
  RecaptchaVerifier,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  setPersistence,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  UserCredential,
  verifyPasswordResetCode,
} from 'firebase/auth';
import {
  getFirebaseAppleAuthProvider,
  getFirebaseAuth,
  getFirebaseGoogleAuthProvider,
} from './config';
import {
  AppleUserData,
  CheckMFA,
  CustomClaimsResult,
  FIREBASE_ERROR_CODES,
  FirebaseErrorCustomData,
  FirebaseLocale,
  FirebaseSmsMfa,
  GenerateCustomTokenArgs,
  GoogleSignUpWithCredentialsProvider,
  GoogleUserData,
  OptionalUserData,
  ProviderOutputWithRole,
  Roles,
  UserHasuraClaims,
  UsersWithRole,
  ValidateCustomTokenArgs,
} from './interfaces';
import {
  useAccountDeletionMutation,
  useGenerateCustomTokenMutation,
  useGetDelegatedAdminByIdLazyQuery,
  useGetUsersByEmailLazyQuery,
  User_Auth_Provider_Types_Enum,
  Users,
  useSignupDelegatedAdminMutation,
  useSignupProviderMutation,
  useSignupUserMutation,
  useValidateCustomTokenMutation,
} from 'graphql/generated/hasura';
import { LegalDocument } from 'graphql/generated/strapi';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  BLOCKED_LOGIN_EXPIRY_TIME,
  IFRAME_RECAPTCHA_CONTENT,
  LOCAL_STORAGE_LOGIN_BLOCKED_TIME,
  pageIds,
} from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { FirebaseError } from 'firebase/app';
import {
  ALLOWED_ROLES_CUSTOM_CLAIM,
  HASURA_USER_STORAGE_KEY,
  JWT_CUSTOM_CLAIMS,
} from './constants';
import { AuthContext, useAuth } from 'auth/context/AuthContext';
import { useAcceptLegalDocument } from 'hooks/legalDocuments/useAcceptLegalDocument';
import { useRegisterAuthProvider } from 'hooks/authProvider/useRegisterAuthProvider';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AUTH_ACCOUNT_DELETED,
  AUTH_EMAIL_NOT_VERIFICATION,
  AUTH_LOGIN,
  AUTH_MFA_REQUIRES_RECENT_LOGIN,
  AUTH_MFA_VERIFY,
  DASHBOARD,
  LOGOUT,
  MY_ACCOUNT_MFA_ACCOUNT_DELETION,
} from 'utilities/routes';
import { REGISTER_AUTH_PROVIDER_ERRORS } from 'hooks/authProvider/interfaces/authProvider.interfaces';
import {
  useGetFhirPatientbyEmailLazyQuery,
  useGetFhirProviderbyEmailLazyQuery,
  useGetProvidersByPatientIdLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
// import { useStrapi } from 'apollo/LocalesProvider';
import { useDataProvider } from 'apollo/DataProvider';
import StorageService from '../utilities/storageService';
import { useRootConfig } from '../utilities/rootConfig';
import queryString from 'query-string';
import { APPOINTMENT_STORAGE } from '../app/my-appointments/components/scheduled-a-appointment/routePath';
import { getFirebaseLocalizedMsg } from '../utilities/firebaseError';
import { Buffer } from 'buffer';
import { useCustomToast } from '../layout/useToast';
import { useUpdateFirebase } from '../hooks/authProvider/useUpdateFirebase';

const storageBlockTime = new StorageService(LOCAL_STORAGE_LOGIN_BLOCKED_TIME);

export const useFetchUserByEmail = () => {
  const [
    fetchUserByEmail,
    {
      data: hasuraUserData,
      loading: hasuraUserLoading,
      error: hasuraUserError,
    },
  ] = useGetUsersByEmailLazyQuery({
    fetchPolicy: 'network-only',
  });

  const fetchUserByEmailPromise = async (
    email: string,
  ): Promise<Partial<Users>> => {
    const data = await fetchUserByEmail({ variables: { email } });
    if (!data.data?.users.length) {
      throw new Error('User not found in hasura');
    }
    const hasuraUser = data.data.users[0];
    return {
      id: hasuraUser.id,
      SENSITIVE_email: hasuraUser.SENSITIVE_email,
      SENSITIVE_profile_picture_id: hasuraUser.SENSITIVE_profile_picture_id,
      SENSITIVE_lastname: hasuraUser.SENSITIVE_lastname,
      SENSITIVE_firstname: hasuraUser.SENSITIVE_firstname,
      SENSITIVE_phone: hasuraUser.SENSITIVE_phone,
      SENSITIVE_country: hasuraUser.SENSITIVE_country,
      account_closed_at: hasuraUser.account_closed_at,
      blocked_at: hasuraUser.blocked_at,
      account_closure_reason: hasuraUser.account_closure_reason,
    };
  };

  return {
    hasuraUserData,
    hasuraUserError,
    hasuraUserLoading,
    fetchUserByEmailPromise,
  };
};

export const useFetchUserFromHasuraOrFhir = () => {
  const [
    getFhirPatientByEmail,
    { data: fhirUserData, loading: fhirUserLoading, error: fhirUserError },
  ] = useGetFhirPatientbyEmailLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    onError: console.warn,
    fetchPolicy: 'network-only',
  });

  const [getDelegatedAdmin] = useGetDelegatedAdminByIdLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'network-only',
  });

  const [getFhirProviderByEmail] = useGetFhirProviderbyEmailLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'network-only',
  });

  const [getProviderByPatientId] = useGetProvidersByPatientIdLazyQuery({
    context: {
      ignoreGlobalErrors: true,
    },
    fetchPolicy: 'network-only',
  });

  const fetchProvidersByPatientId = async (id: string, role: Roles) => {
    if (role !== Roles.PATIENT) {
      return undefined;
    }
    const data = await getProviderByPatientId({
      variables: { patientCodexId: id },
    });

    if (!data.data?.getProvidersByPatientId?.providers?.length) {
      return undefined;
    }

    const fhirProvider = data.data.getProvidersByPatientId?.providers[0];
    return fhirProvider.id;
  };

  const fetchUserDataFromDifferentSource = async (
    email: string,
    role: Roles,
  ): Promise<Partial<UsersWithRole | ProviderOutputWithRole> | null> => {
    if (role === Roles.PROVIDER && email) {
      const data = await getFhirProviderByEmail({ variables: { email } });

      if (!data.data?.getFHIRProviderbyEmail?.providers?.length) {
        throw new Error('Provider not found in fhir');
      }

      const fhirProvider = data.data.getFHIRProviderbyEmail?.providers[0];

      return {
        id: fhirProvider.id,
        SENSITIVE_email: fhirProvider.SENSITIVE_email,
        SENSITIVE_profile_picture_id: fhirProvider.SENSITIVE_profile_picture_id,
        SENSITIVE_lastname: fhirProvider.SENSITIVE_lastname,
        SENSITIVE_firstname: fhirProvider.SENSITIVE_firstname,
        SENSITIVE_phone: fhirProvider.SENSITIVE_phone,
        SENSITIVE_gender: fhirProvider.SENSITIVE_gender,
        SENSITIVE_address_information:
          fhirProvider.SENSITIVE_address_information,
        role: role,
        enabled: fhirProvider.fully_verified,
      } as unknown as ProviderOutputWithRole;
    } else if (role === Roles.PATIENT && email) {
      const data = await getFhirPatientByEmail({ variables: { email } });
      if (!data.data?.getFHIRPatientbyEmail?.users?.length) {
        throw new Error('Patient User not found in fhir');
      }

      const fhirPatient = data.data.getFHIRPatientbyEmail.users[0];
      return {
        id: fhirPatient.id,
        SENSITIVE_email: fhirPatient.SENSITIVE_email as string,
        SENSITIVE_profile_picture_id: fhirPatient.SENSITIVE_profile_picture_id,
        SENSITIVE_lastname: fhirPatient.SENSITIVE_lastname,
        SENSITIVE_firstname: fhirPatient.SENSITIVE_firstname,
        SENSITIVE_country: fhirPatient.SENSITIVE_country,
        SENSITIVE_phone: fhirPatient.SENSITIVE_phone,
        SENSITIVE_dob: fhirPatient.SENSITIVE_dob,
        SENSITIVE_gender: fhirPatient.SENSITIVE_gender,
        SENSITIVE_self_identity_gender:
          fhirPatient.SENSITIVE_self_identity_gender,
        SENSITIVE_address_information:
          fhirPatient.SENSITIVE_address_information,
        role: role || Roles.PATIENT,
        parentId: fhirPatient.parentCodexId,
      };
    }
    return null;
  };

  return {
    fhirUserData,
    fhirUserError,
    fhirUserLoading,
    fetchUserDataFromDifferentSource,
    fetchProvidersByPatientId,
    getDelegatedAdmin,
  };
};

export const useSignupUserInHasura = () => {
  const [signupUsersMutation, { error: registerUserError }] =
    useSignupUserMutation();

  const signupUserInHasura = async (
    email: string,
    token?: string,
    optionalUserData?: OptionalUserData,
  ): Promise<void> => {
    try {
      const response = await signupUsersMutation({
        variables: {
          email: email,
          firebaseToken: token,
          ...optionalUserData,
        },
      });
      if (!response.data) {
        throw new Error('Failed to create user');
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('Failed to create user');
    }
  };

  return { signupUserInHasura, registerUserError };
};

export const useSignupProviderInHasura = () => {
  const [signupProviderMutation, { error: registerUserProviderError }] =
    useSignupProviderMutation({
      onError: ({ message }) => {
        throw new Error(message);
      },
    });

  const signupProviderInHasura = async (
    email: string,
    password: string,
  ): Promise<void> => {
    const response = await signupProviderMutation({
      variables: {
        email,
        password,
      },
    });
    if (response.errors) {
      console.error(response.errors);
      throw new Error('Failed to create provider');
    }
  };

  return { signupProviderInHasura, registerUserProviderError };
};

export const useGoogleAuthSignInPopUp = (): [
  GoogleUserData | object,
  string,
  (documents: LegalDocument[]) => Promise<void>,
] => {
  const firebaseAuth = getFirebaseAuth();
  const firebaseGoogleAuthProvider = getFirebaseGoogleAuthProvider();
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [userData, setUserData] = useState<GoogleUserData | object>({});
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { signupUserInHasura, registerUserError } = useSignupUserInHasura();
  const { loginWithOutMFA, setUserUuid, setPhoneNumber, setCountry } =
    useAuth();
  const { acceptLegalDocumentError } = useAcceptLegalDocument();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const { registerAuthProvider, registerProviderError } =
    useRegisterAuthProvider();
  const navigate = useNavigate();
  const { setMfaResolver } = useContext(AuthContext);

  const { updateFirebaseUserEmail } = useUpdateFirebase();

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const result = await signInWithPopup(
        firebaseAuth,
        firebaseGoogleAuthProvider,
      );

      const providerEmail = result.user.providerData?.[0]?.email as string;
      const userEmail = result.user.email;

      if (!userEmail && providerEmail) {
        const token = await result.user.getIdToken(true);
        await updateFirebaseUserEmail({
          newEmail: providerEmail,
          firebaseToken: token,
        });
        await result.user.reload();
      }

      const email = userEmail || providerEmail || '';

      if (email) {
        const tokenRes = await result.user.getIdTokenResult();
        setToken(tokenRes.token);
        setErrorMsg('');
        setUserData({
          displayName: result.user.displayName,
          email: email,
          uid: result.user.uid,
        });
        const isAlreadyRegistered = await userHasCustomClaims();
        if (!isAlreadyRegistered) {
          try {
            await signupUserInHasura(email, tokenRes.token);
          } catch (e) {
            console.log(e);
          }
        }

        const credential = GoogleAuthProvider.credentialFromResult(result);

        if (!credential) return;

        await signInWithCredential(firebaseAuth, credential);

        const user = await fetchUserByEmailPromise(email);
        await registerAuthProvider(User_Auth_Provider_Types_Enum.Google);
        setUserUuid(user.id);

        if (user.SENSITIVE_phone) {
          setPhoneNumber(user.SENSITIVE_phone);
        }

        if (user.SENSITIVE_country) {
          setCountry(user.SENSITIVE_country);
        }

        loginWithOutMFA(DASHBOARD);
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED: {
            const userEmail = (
              error?.customData?._serverResponse as FirebaseErrorCustomData
            ).email;
            const resolver = getMultiFactorResolver(
              firebaseAuth,
              error as MultiFactorError,
            );

            setMfaResolver(resolver);
            const deepLink = location.search;
            navigate(`${AUTH_MFA_VERIFY}${deepLink ? deepLink : ''}`, {
              state: { email: userEmail },
            });

            break;
          }
          default:
            setErrorMsg(locale?.unexpectedError);
            break;
        }
      } else if (error instanceof Error) {
        error.message ===
        REGISTER_AUTH_PROVIDER_ERRORS.AUTH_PROVIDER_ALREADY_EXISTS
          ? loginWithOutMFA(DASHBOARD)
          : setErrorMsg(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
    }
    if (registerUserError) {
      setErrorMsg(registerUserError.message);
    }
    if (acceptLegalDocumentError) {
      setErrorMsg(acceptLegalDocumentError.message);
    }
    if (registerProviderError) {
      setErrorMsg(registerProviderError.message);
    }
  };
  return [{ ...userData, token }, errorMsg, handleGoogleSignIn];
};

export const useGoogleAuthProviderSignInPopUp = (): [
  GoogleUserData | object,
  string,
  (documents: LegalDocument[]) => Promise<void>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [userData, setUserData] = useState<GoogleUserData | object>({});
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { registerUserProviderError, signupProviderInHasura } =
    useSignupProviderInHasura();
  const { loginWithOutMFA, setUserUuid, setPhoneNumber, setCountry } =
    useAuth();
  const { acceptLegalDocumentError } = useAcceptLegalDocument();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const { registerAuthProvider, registerProviderError } =
    useRegisterAuthProvider();
  const navigate = useNavigate();
  const { setMfaResolver } = useContext(AuthContext);
  const firebaseAuth = getFirebaseAuth();
  const firebaseGoogleAuthProvider = getFirebaseGoogleAuthProvider();

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const result = await signInWithPopup(
        firebaseAuth,
        firebaseGoogleAuthProvider,
      );

      if (result.user.email) {
        const tokenRes = await result.user.getIdTokenResult();
        setToken(tokenRes.token);
        setErrorMsg('');
        setUserData({
          email: result.user.email,
          uid: result.user.uid,
        });
        const isAlreadyRegistered = await userHasCustomClaims();
        if (!isAlreadyRegistered) {
          await signupProviderInHasura(result.user.email, tokenRes.token);
          await result.user.getIdToken(true);
        }
        const user = await fetchUserByEmailPromise(result.user.email);
        await registerAuthProvider(User_Auth_Provider_Types_Enum.Google);
        setUserUuid(user.id);

        if (user.SENSITIVE_phone) {
          setPhoneNumber(user.SENSITIVE_phone);
        }

        if (user.SENSITIVE_country) {
          setCountry(user.SENSITIVE_country);
        }

        loginWithOutMFA(DASHBOARD);
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED: {
            const userEmail = (
              error?.customData?._serverResponse as FirebaseErrorCustomData
            ).email;
            const resolver = getMultiFactorResolver(
              firebaseAuth,
              error as MultiFactorError,
            );

            setMfaResolver(resolver);
            const deepLink = location.search;
            navigate(`${AUTH_MFA_VERIFY}${deepLink ? deepLink : ''}`, {
              state: { email: userEmail },
            });

            break;
          }
          default:
            setErrorMsg(locale?.unexpectedError);
            break;
        }
      } else if (error instanceof Error) {
        error.message ===
        REGISTER_AUTH_PROVIDER_ERRORS.AUTH_PROVIDER_ALREADY_EXISTS
          ? loginWithOutMFA(DASHBOARD)
          : setErrorMsg(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
    }
    if (registerUserProviderError) {
      setErrorMsg(registerUserProviderError.message);
    }
    if (acceptLegalDocumentError) {
      setErrorMsg(acceptLegalDocumentError.message);
    }
    if (registerProviderError) {
      setErrorMsg(registerProviderError.message);
    }
  };
  return [{ ...userData, token }, errorMsg, handleGoogleSignIn];
};

export const useGoogleSignupWithCredentials = (): [
  GoogleUserData | object,
  string,
  (
    email: string,
    password: string,
    documents: LegalDocument[],
    optionalUserData?: OptionalUserData,
  ) => Promise<void>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [userData, setUserData] = useState<GoogleUserData | object>({});
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { signupUserInHasura, registerUserError } = useSignupUserInHasura();
  const { acceptLegalDocumentError } = useAcceptLegalDocument();
  const [, handleSendVerificationEmail] = useSendVerificationEmail();
  const { registerAuthProvider, registerProviderError } =
    useRegisterAuthProvider();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  let { baseUrl } = useRootConfig();
  const location = useLocation();
  const { fetchUserDataFromDifferentSource } = useFetchUserFromHasuraOrFhir();
  const hasuraUserStorage = new StorageService(HASURA_USER_STORAGE_KEY);
  const firebaseAuth = getFirebaseAuth();
  const { updateFirebaseUserData } = useUpdateFirebase();

  useEffect(() => {
    if (registerUserError) {
      setErrorMsg(registerUserError.message);
    } else if (acceptLegalDocumentError) {
      setErrorMsg(acceptLegalDocumentError.message);
    } else if (registerProviderError) {
      setErrorMsg(registerProviderError.message);
    }
  }, [registerUserError, acceptLegalDocumentError, registerProviderError]);

  const handleGoogleSignupWithCredentials = async (
    email: string,
    password: string,
    documents: LegalDocument[],
    optionalUserData?: OptionalUserData,
  ): Promise<void> => {
    try {
      setErrorMsg('');
      await setPersistence(firebaseAuth, browserLocalPersistence);
      await signupUserInHasura(email, undefined, {
        ...optionalUserData,
        password,
      });

      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );
      const token = await userCredential.user.getIdToken(true);

      await updateFirebaseUserData({
        firstname: optionalUserData?.firstname ?? '',
        lastname: optionalUserData?.lastname ?? '',
        firebaseToken: token,
      });

      const { source } = queryString.parse(location.search);
      if (source) {
        baseUrl = `${baseUrl}?source=${encodeURIComponent(source.toString())}`;
      }
      await sendEmailVerification(userCredential.user, {
        url: baseUrl,
        handleCodeInApp: true,
      });

      if (userCredential.user.email) {
        setUserData({
          displayName: userCredential.user.displayName,
          email: userCredential.user.email,
          uid: userCredential.user.uid,
          ...optionalUserData,
        });
        setErrorMsg('');
        const tokenRes = await userCredential.user.getIdTokenResult();
        setToken(tokenRes.token);

        await handleSendVerificationEmail(userCredential.user.email, false);
        await userCredential.user.getIdToken(true);
        await registerAuthProvider(User_Auth_Provider_Types_Enum.Email);
        const user = await fetchUserByEmailPromise(email);
        const userDataFromSource = await fetchUserDataFromDifferentSource(
          userCredential.user.email || '',
          Roles.PATIENT,
        );
        hasuraUserStorage.setData({ ...user, ...userDataFromSource });
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
    } catch (error: unknown) {
      console.error(error);
      if (error instanceof FirebaseError) {
        setErrorMsg(getFirebaseLocalizedMsg(error, locale) as string);
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
      }
    }
  };

  return [{ ...userData, token }, errorMsg, handleGoogleSignupWithCredentials];
};

export const useGoogleSignupWithCredentialsForProvider =
  (): GoogleSignUpWithCredentialsProvider => {
    const { data: locale } = useGetPage({
      locale: 'en',
      pageId: pageIds.FIREBASE_ERRORS,
    });
    const [userData, setUserData] = useState<Record<string, unknown>>();
    const [token, setToken] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [errorCode, setErrorCode] = useState<string>('');
    const { signupProviderInHasura } = useSignupProviderInHasura();
    const [, handleSendVerificationEmail] = useSendVerificationEmail();
    const firebaseAuth = getFirebaseAuth();

    const [loading, setLoading] = useState(false);

    const handleGoogleSignupWithCredentials = async (
      email: string,
      password: string,
    ): Promise<void> => {
      try {
        setLoading(true);
        await setPersistence(firebaseAuth, browserLocalPersistence);
        await signupProviderInHasura(email, password);
        const userCredential = await signInWithEmailAndPassword(
          firebaseAuth,
          email,
          password,
        );
        if (userCredential.user.email) {
          setUserData({
            uid: userCredential.user.uid,
          });
          setErrorMsg('');
          setErrorCode('');
          const tokenRes = await userCredential.user.getIdTokenResult();

          await handleSendVerificationEmail(userCredential.user.email, true);
          await userCredential.user.getIdToken(true);
          setToken(tokenRes.token);
          setLoading(false);
        } else {
          setErrorMsg(locale?.unexpectedError);
          setLoading(false);
        }
      } catch (error: unknown) {
        console.error(error);
        setErrorMsg(locale?.unexpectedError);
        setLoading(false);
        if (error instanceof FirebaseError) {
          setErrorMsg(getFirebaseLocalizedMsg(error, locale) as string);
          setErrorCode(error.code);
        } else if (error instanceof Error) {
          setErrorMsg(error.message);
        } else {
          setErrorMsg(locale?.unexpectedError);
        }
        setLoading(false);
      }
    };

    return [
      { ...userData, token },
      errorMsg,
      loading,
      errorCode,
      handleGoogleSignupWithCredentials,
    ];
  };

export const useGoogleSignupWithCredentialsForAdmin = () => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const { addToast } = useCustomToast();
  const [userData, setUserData] = useState<Record<string, unknown>>();
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [signUpAdmin] = useSignupDelegatedAdminMutation({
    onCompleted: (data) => {
      addToast({
        type: 'success',
        message: data?.SignupDelegatedAdmin?.message,
      });
    },
  });

  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const { updateFirebaseUserData } = useUpdateFirebase();
  const hasuraUserStorage = new StorageService(HASURA_USER_STORAGE_KEY);
  const [loading, setLoading] = useState(false);
  const firebaseAuth = getFirebaseAuth();

  const handleGoogleSignupWithCredentials = async (
    email: string,
    password: string,
    token: string,
    firstname: string,
    lastname: string,
  ): Promise<void> => {
    try {
      setLoading(true);
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );
      if (userCredential.user.email) {
        const firebaseToken = await userCredential.user.getIdToken(true);

        await updateFirebaseUserData({
          firstname: firstname ?? '',
          lastname: lastname ?? '',
          firebaseToken: firebaseToken,
        });

        setErrorMsg('');
        setErrorCode('');
        setUserData({
          displayName: userCredential.user.displayName,
          email: userCredential.user.email,
          uid: userCredential.user.uid,
        });
        await signUpAdmin({
          variables: {
            token: token,
            firebaseToken: firebaseToken,
            firstname,
            lastname,
          },
        });
        await userCredential.user.getIdToken(true);
        setLoading(false);
        const user = await fetchUserByEmailPromise(email);
        hasuraUserStorage.setData({ ...user });

        await signOut(firebaseAuth);

        setToken('');
        setLoading(false);
      } else {
        setErrorMsg(locale?.unexpectedError);
        setLoading(false);
      }
    } catch (error: unknown) {
      console.error(error);
      setErrorMsg(locale?.unexpectedError);
      setLoading(false);
      if (error instanceof FirebaseError) {
        setErrorMsg(getFirebaseLocalizedMsg(error, locale) as string);
        setErrorCode(error.code);
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
      setLoading(false);
    }
  };

  return {
    loading,
    handleGoogleSignupWithCredentials,
    token,
    errorCode,
    errorMsg,
    userData,
  };
};

export const useGoogleSigninWithCredentials = (): [
  GoogleUserData | object,
  string,
  (email: string, password: string) => Promise<boolean>,
] => {
  const location = useLocation();
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const firebaseAuth = getFirebaseAuth();

  const [userData, setUserData] = useState<GoogleUserData | object>({});
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { loginWithOutMFA, setUserUuid, setPhoneNumber, setCountry } =
    useAuth();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const navigate = useNavigate();
  const { setMfaResolver } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const encodedRedirect = searchParams.get('redirect');

  const redirect =
    encodedRedirect &&
    Buffer.from(decodeURIComponent(encodedRedirect), 'base64').toString(
      'utf-8',
    );

  const handleGoogleSigninWithCredentials = async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );

      if (userCredential.user.emailVerified) {
        if (userCredential.user.email) {
          setUserData({
            displayName: userCredential.user.displayName,
            email: userCredential.user.email,
            uid: userCredential.user.uid,
          });
          setErrorMsg('');
          const tokenRes = await userCredential.user.getIdTokenResult();
          setToken(tokenRes.token);
          const user = await fetchUserByEmailPromise(userCredential.user.email);
          setUserUuid(user.id);

          if (user.SENSITIVE_phone) {
            setPhoneNumber(user.SENSITIVE_phone);
          }

          if (user.SENSITIVE_country) {
            setCountry(user.SENSITIVE_country);
          }

          // if (process.env.REACT_APP_IS_DEBUG_MODE) {
          //   loginWithOutMFA(DASHBOARD);
          //   return true;
          // }
          loginWithOutMFA((redirect as string) || DASHBOARD);
          return true;
        }
      } else {
        navigate(AUTH_EMAIL_NOT_VERIFICATION);
      }

      return false;
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED: {
            const resolver = getMultiFactorResolver(
              firebaseAuth,
              error as MultiFactorError,
            );

            setMfaResolver(resolver);
            const deepLink = location.search;
            navigate(`${AUTH_MFA_VERIFY}${deepLink ? deepLink : ''}`, {
              state: { email },
            });

            break;
          }
          case FIREBASE_ERROR_CODES.AUTH_WRONG_PASSWORD:
          case FIREBASE_ERROR_CODES.AUTH_USER_NOT_FOUND:
            setErrorMsg(locale?.invalidCredentials);
            break;
          case FIREBASE_ERROR_CODES.AUTH_TOO_MANY_REQUESTS:
            storageBlockTime.setData(BLOCKED_LOGIN_EXPIRY_TIME);
            setErrorMsg(locale?.tooManyRequests);
            break;
          default:
            setErrorMsg(locale?.unexpectedError);
            break;
        }
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
      return false;
    }
  };
  return [{ ...userData, token }, errorMsg, handleGoogleSigninWithCredentials];
};

/**
 * Apple Auth Hooks
 *
 */

export const useAppleAuthSignInPopUp = (): [
  AppleUserData | object,
  string,
  (documents: LegalDocument[]) => Promise<void>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [userData, setUserData] = useState<GoogleUserData | object>({});
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { signupUserInHasura, registerUserError } = useSignupUserInHasura();
  const { loginWithOutMFA, setUserUuid, setPhoneNumber, setCountry } =
    useAuth();
  const { acceptLegalDocumentError } = useAcceptLegalDocument();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const { registerAuthProvider, registerProviderError } =
    useRegisterAuthProvider();
  const { setMfaResolver } = useContext(AuthContext);
  const navigate = useNavigate();
  const firebaseAuth = getFirebaseAuth();
  const { updateFirebaseUserEmail } = useUpdateFirebase();

  const firebaseAppleAuthProvider = getFirebaseAppleAuthProvider();
  const handleAppleSignIn = async (): Promise<void> => {
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const result = await signInWithPopup(
        firebaseAuth,
        firebaseAppleAuthProvider,
      );

      const providerEmail = result.user.providerData?.[0]?.email;
      const userEmail = result.user.email;

      if (!userEmail && providerEmail) {
        const token = await result.user.getIdToken(true);
        await updateFirebaseUserEmail({
          newEmail: providerEmail,
          firebaseToken: token,
        });
        await result.user.reload();
      }

      const email = userEmail || providerEmail || '';

      const tokenRes = await result.user.getIdTokenResult();
      setToken(tokenRes.token);
      setErrorMsg('');
      setUserData({
        email: email,
        uid: result.user.uid,
      });

      const isAlreadyRegistered = await userHasCustomClaims();
      if (!isAlreadyRegistered) {
        try {
          await signupUserInHasura(email, tokenRes.token);
        } catch (e) {
          console.log(e);
        }
        await result.user.getIdToken(true);
      }

      const credential = OAuthProvider.credentialFromResult(result);

      if (!credential) return;

      await signInWithCredential(firebaseAuth, credential);

      const user = await fetchUserByEmailPromise(email);
      await registerAuthProvider(User_Auth_Provider_Types_Enum.Apple);
      setUserUuid(user.id);

      if (user.SENSITIVE_phone) {
        setPhoneNumber(user.SENSITIVE_phone);
      }

      if (user.SENSITIVE_country) {
        setCountry(user.SENSITIVE_country);
      }

      loginWithOutMFA(DASHBOARD);
    } catch (error) {
      console.log(error);
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED: {
            const userEmail = (
              error?.customData?._serverResponse as FirebaseErrorCustomData
            ).email;
            const resolver = getMultiFactorResolver(
              firebaseAuth,
              error as MultiFactorError,
            );

            setMfaResolver(resolver);
            const deepLink = location.search;
            navigate(`${AUTH_MFA_VERIFY}${deepLink ? deepLink : ''}`, {
              state: { email: userEmail },
            });

            break;
          }
          default:
            setErrorMsg(locale?.unexpectedError);
            break;
        }
      } else if (error instanceof Error) {
        error.message ===
        REGISTER_AUTH_PROVIDER_ERRORS.AUTH_PROVIDER_ALREADY_EXISTS
          ? loginWithOutMFA(DASHBOARD)
          : setErrorMsg(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
    }
    if (registerUserError) {
      setErrorMsg(registerUserError.message);
    }
    if (acceptLegalDocumentError) {
      setErrorMsg(acceptLegalDocumentError.message);
    }
    if (registerProviderError) {
      setErrorMsg(registerProviderError.message);
    }
  };
  return [{ ...userData, token }, errorMsg, handleAppleSignIn];
};

export const useAppleAuthProviderSignInPopUp = (): [
  AppleUserData | object,
  string,
  (documents: LegalDocument[]) => Promise<void>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [userData, setUserData] = useState<GoogleUserData | object>({});
  const [token, setToken] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { registerUserProviderError, signupProviderInHasura } =
    useSignupProviderInHasura();
  const { loginWithOutMFA, setUserUuid, setPhoneNumber, setCountry } =
    useAuth();
  const { acceptLegalDocumentError } = useAcceptLegalDocument();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const { registerAuthProvider, registerProviderError } =
    useRegisterAuthProvider();
  const { setMfaResolver } = useContext(AuthContext);
  const navigate = useNavigate();
  const firebaseAuth = getFirebaseAuth();
  const firebaseAppleAuthProvider = getFirebaseAppleAuthProvider();

  const handleAppleSignIn = async (): Promise<void> => {
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const result = await signInWithPopup(
        firebaseAuth,
        firebaseAppleAuthProvider,
      );

      if (result.user.email) {
        const tokenRes = await result.user.getIdTokenResult();
        setToken(tokenRes.token);
        setErrorMsg('');
        setUserData({
          email: result.user.email,
          uid: result.user.uid,
        });

        const isAlreadyRegistered = await userHasCustomClaims();
        if (!isAlreadyRegistered) {
          await signupProviderInHasura(result.user.email, tokenRes.token);
          await result.user.getIdToken(true);
        }
        const user = await fetchUserByEmailPromise(result.user.email);
        await registerAuthProvider(User_Auth_Provider_Types_Enum.Apple);
        setUserUuid(user.id);

        if (user.SENSITIVE_phone) {
          setPhoneNumber(user.SENSITIVE_phone);
        }

        if (user.SENSITIVE_country) {
          setCountry(user.SENSITIVE_country);
        }

        loginWithOutMFA(DASHBOARD);
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED: {
            const userEmail = (
              error?.customData?._serverResponse as FirebaseErrorCustomData
            ).email;
            const resolver = getMultiFactorResolver(
              firebaseAuth,
              error as MultiFactorError,
            );

            setMfaResolver(resolver);
            const deepLink = location.search;
            navigate(`${AUTH_MFA_VERIFY}${deepLink ? deepLink : ''}`, {
              state: { email: userEmail },
            });

            break;
          }
          default:
            setErrorMsg(locale?.unexpectedError);
            break;
        }
      } else if (error instanceof Error) {
        error.message ===
        REGISTER_AUTH_PROVIDER_ERRORS.AUTH_PROVIDER_ALREADY_EXISTS
          ? loginWithOutMFA(DASHBOARD)
          : setErrorMsg(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
    }
    if (registerUserProviderError) {
      setErrorMsg(registerUserProviderError.message);
    }
    if (acceptLegalDocumentError) {
      setErrorMsg(acceptLegalDocumentError.message);
    }
    if (registerProviderError) {
      setErrorMsg(registerProviderError.message);
    }
  };
  return [{ ...userData, token }, errorMsg, handleAppleSignIn];
};

export const usePasswordResetEmail = (
  skipNotFoundUserError?: boolean,
): [string, (email: string) => Promise<boolean>, boolean] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const firebaseAuth = getFirebaseAuth();

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const handlePasswordResetEmail = async (email: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(firebaseAuth, email, {
        url: `${window.location.origin}?email=${email}`,
      });
      setErrorMsg('');
      return true;
    } catch (error: unknown) {
      const code = error instanceof FirebaseError ? error.code : '';

      if (code === FIREBASE_ERROR_CODES.AUTH_TOO_MANY_REQUESTS) {
        setErrorMsg(locale?.exceededLimit);
        return false;
      }

      if (
        code === FIREBASE_ERROR_CODES.AUTH_USER_NOT_FOUND &&
        skipNotFoundUserError
      ) {
        return false;
      }

      setErrorMsg(locale?.failedToSendPasswordEmail);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  return [errorMsg, handlePasswordResetEmail, isLoading];
};

export const useConfirmPasswordReset = (): [
  string,
  (code: string, newPassword: string) => Promise<boolean>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [errorMsg, setErrorMsg] = useState<string>('');
  const firebaseAuth = getFirebaseAuth();

  const handleConfirmPasswordReset = async (
    code: string,
    newPassword: string,
  ): Promise<boolean> => {
    try {
      await confirmPasswordReset(firebaseAuth, code, newPassword);
      return true;
    } catch (error: unknown) {
      setErrorMsg(locale?.failedToConfirmPasswordReset);
      return false;
    }
  };
  return [errorMsg, handleConfirmPasswordReset];
};

export const useFirebaseSignOut = () => {
  const navigate = useNavigate();
  const { client: hasura } = useDataProvider();
  const firebaseAuth = getFirebaseAuth();

  return useCallback(
    async (path: string | undefined = AUTH_LOGIN) => {
      const appointmentStorage = new StorageService(
        APPOINTMENT_STORAGE,
        sessionStorage,
      );

      try {
        await hasura.clearStore();
        appointmentStorage.clear();
        // it will break hook --- > useGetPage() ---> loading property always true (NetworkRequiredRoute) --> as result white screen
        //  strapi.clearStore();
        await signOut(firebaseAuth);
        navigate(path);
      } catch (error: unknown) {
        console.error('Error signing out: ', error);
      }
    },
    [firebaseAuth, hasura, navigate],
  );
};

export const getTokenFromFirebase = async () => {
  const firebaseAuth = getFirebaseAuth();

  try {
    const user = firebaseAuth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      return token;
    }
  } catch (error: unknown) {
    console.error('Error when try to fetch token from Firebase:', error);
  }
  return null;
};

export const userHasCustomClaims = async (): Promise<boolean> => {
  const firebaseAuth = getFirebaseAuth();

  try {
    const user = firebaseAuth.currentUser;

    if (user) {
      const idTokenResult = await user.getIdTokenResult();
      const claims = idTokenResult.claims;
      return JWT_CUSTOM_CLAIMS in claims;
    }
    return false;
  } catch (error: unknown) {
    console.error('Error when try to get id token:', error);
    return false;
  }
};

export const userHasCustomClaimsAndRole =
  async (): Promise<CustomClaimsResult> => {
    const firebaseAuth = getFirebaseAuth();

    try {
      const user = firebaseAuth.currentUser;

      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;

        const customClaims = claims[JWT_CUSTOM_CLAIMS] as UserHasuraClaims;

        if (
          customClaims &&
          Object.prototype.hasOwnProperty.call(
            customClaims,
            ALLOWED_ROLES_CUSTOM_CLAIM,
          )
        ) {
          const allowedRoles = customClaims[ALLOWED_ROLES_CUSTOM_CLAIM] || [];
          const hasCustomClaims = JWT_CUSTOM_CLAIMS in claims;
          const role =
            allowedRoles.length > 0
              ? allowedRoles[allowedRoles.length - 1]
              : '';

          return { hasCustomClaims, role };
        }
      }
      return { hasCustomClaims: false, role: '' };
    } catch (error: unknown) {
      console.error('Error when trying to get id token:', error);
      return { hasCustomClaims: false, role: '' };
    }
  };

export const useSendVerificationEmail = (): [
  string,
  (email: string, isProvider?: boolean) => Promise<boolean>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [errorMsg, setErrorMsg] = useState<string>('');
  const firebaseAuth = getFirebaseAuth();
  const handleSendVerificationEmail = async (
    email: string,
    isProvider?: boolean,
  ): Promise<boolean> => {
    try {
      const user = firebaseAuth.currentUser;

      if (!user) {
        setErrorMsg('You must be logged in to request a verification email.');
        return false;
      }

      const signInMethods = await fetchSignInMethodsForEmail(
        firebaseAuth,
        email,
      );
      if (signInMethods.length === 0) {
        setErrorMsg(
          'No account found with this email addres. Check the entry or use another one',
        );
        return false;
      }

      isProvider
        ? await sendEmailVerification(user, {
            url: `${window.location.origin}/auth/sign-up-provider?email=${email}`,
          })
        : await sendEmailVerification(user, {
            url: `${window.location.origin}?email=${email}`,
          });

      setErrorMsg('');
      return true;
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case FIREBASE_ERROR_CODES.AUTH_TOO_MANY_REQUESTS:
            setErrorMsg(locale?.tooManyRequestsEmail);
            break;
          case FIREBASE_ERROR_CODES.AUTH_EMAIL_ALREADY_IN_USE:
            setErrorMsg('This email is already in use by another account.');
            break;
          case FIREBASE_ERROR_CODES.AUTH_REQUIRES_RECENT_LOGIN:
            setErrorMsg(
              'For security, please log out and log back in with your original email/password to change your email.',
            );
            break;
          default:
            setErrorMsg(locale?.failedToSendVerificationEmail);
            break;
        }

        return false;
      }

      setErrorMsg(locale?.failedToSendVerificationEmail);
      return false;
    }
  };
  return [errorMsg, handleSendVerificationEmail];
};

export const useVerifyActionCode = (): [
  string,
  (oobCode: string) => Promise<boolean>,
] => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [errorMsg, setErrorMsg] = useState<string>('');
  const firebaseAuth = getFirebaseAuth();

  const handleVerifyActionCode = async (oobCode: string): Promise<boolean> => {
    try {
      await applyActionCode(firebaseAuth, oobCode);
      setErrorMsg('');
      return true;
    } catch (error: unknown) {
      setErrorMsg(locale?.failedToCheckActionCode);
      return false;
    }
  };
  return [errorMsg, handleVerifyActionCode];
};

export const useVerifyPasswordResetCode = (): [
  (oobCode: string) => Promise<boolean>,
] => {
  const firebaseAuth = getFirebaseAuth();

  const handleVerifyPasswordResetCode = async (
    oobCode: string,
  ): Promise<boolean> => {
    try {
      await verifyPasswordResetCode(firebaseAuth, oobCode);
      return true;
    } catch (error: unknown) {
      return false;
    }
  };
  return [handleVerifyPasswordResetCode];
};

export const useGenerateCustomToken = (): GenerateCustomTokenArgs => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });
  const [generateCustomTokenMutation, { data, error, loading }] =
    useGenerateCustomTokenMutation();
  const firebaseAuth = getFirebaseAuth();

  const handleGenerateCustomToken = useCallback(async () => {
    try {
      const user = firebaseAuth.currentUser;
      if (user) {
        const response = await generateCustomTokenMutation();
        if (!response.data) {
          throw new Error(locale?.failedToGenerateCustomToken);
        }
        return response.data.GenerateCustomToken?.token;
      }
    } catch (error: unknown) {
      throw new Error(locale?.failedToGenerateCustomToken);
    }
  }, [firebaseAuth, generateCustomTokenMutation, locale]);

  return { handleGenerateCustomToken, data, error, loading };
};

export const useValidateCustomToken = (): ValidateCustomTokenArgs => {
  const [validateCustomTokenMutation, { data, error }] =
    useValidateCustomTokenMutation();

  const handleValidateCustomToken = useCallback(
    async (token: string) => {
      try {
        const response = await validateCustomTokenMutation({
          variables: {
            token,
          },
        });

        if (!response.data) {
          console.error('There was an error when validating custom token');
          throw new Error('Custom token validation failed');
        }
      } catch (error: unknown) {
        console.error('There was an error when validating custom token');
        throw new Error('Custom token validation failed');
      }
    },
    [validateCustomTokenMutation],
  );

  return { handleValidateCustomToken, data, error };
};

export const userHasRequiredRole = async (
  requiredRole: Roles,
  firebaseAuth: Auth,
): Promise<boolean> => {
  try {
    const user = firebaseAuth.currentUser;
    if (user) {
      const idTokenResult = await user.getIdTokenResult();
      const claims = idTokenResult.claims;
      if (JWT_CUSTOM_CLAIMS in claims) {
        const customClaims = claims[JWT_CUSTOM_CLAIMS] as UserHasuraClaims;
        const userAllowedRoles = customClaims[ALLOWED_ROLES_CUSTOM_CLAIM] ?? [];
        return userAllowedRoles.includes(requiredRole);
      } else {
        return false;
      }
    }
    return false;
  } catch (error: unknown) {
    return false;
  }
};

export const useFirebaseSmsMfa = (
  locale: FirebaseLocale | undefined,
  errorCallback?: (error?: unknown) => void,
): FirebaseSmsMfa => {
  const [isVerified, setVerified] = useState<boolean>(false);
  const [isCaptchaResolved, setIsCaptchaResolved] = useState<boolean>(false);
  const [isCaptchaLoaded, setIsCaptchaLoaded] = useState<boolean>(false);
  const [verificationId, setVerificationId] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | undefined>('');
  const signOutUser = useFirebaseSignOut();
  const recaptchaVerifierInstance = useRef<RecaptchaVerifier | null>(null);
  const { mfaResolver: resolver } = useContext(AuthContext);
  const [accountDeletionMutation] = useAccountDeletionMutation({});

  const navigate = useNavigate();

  let intervalId: string | number | NodeJS.Timer | undefined;
  let observer: MutationObserver | null = null;
  const firebaseAuth = getFirebaseAuth();

  const removeRecaptchaWindow = () => {
    const recaptchaIframes = document.getElementsByTagName('iframe');
    const recaptchaWindows = [...recaptchaIframes]?.filter((x) =>
      x.src.includes(IFRAME_RECAPTCHA_CONTENT),
    );

    recaptchaWindows.forEach((iframe) => {
      iframe.remove();
    });
  };

  const findRecaptchaWindow = () => {
    const recaptchaIframes = document.getElementsByTagName('iframe');
    const recaptchaWindow = [...recaptchaIframes]?.find((x) =>
      x.src.includes(IFRAME_RECAPTCHA_CONTENT),
    )?.parentNode?.parentNode as HTMLDivElement;

    return recaptchaWindow;
  };

  const handlePopState = () => {
    const recaptchaWindow = findRecaptchaWindow();

    if (intervalId) {
      clearInterval(+intervalId);
    }

    if (observer) {
      observer.disconnect();
    }

    if (recaptchaWindow) {
      recaptchaWindow.remove();
    }

    window.removeEventListener('popstate', handlePopState);
  };

  const initObserver = () => {
    let recaptchaWindow = findRecaptchaWindow();

    if (!recaptchaWindow) {
      intervalId = setInterval(() => {
        recaptchaWindow = findRecaptchaWindow();
        if (recaptchaWindow && intervalId) {
          clearInterval(+intervalId);
          setupMutationObserver(recaptchaWindow);
        }
      }, 500);
    } else {
      setupMutationObserver(recaptchaWindow);
    }

    window.addEventListener('popstate', handlePopState);
  };

  const setupMutationObserver = (recaptchaWindow: HTMLDivElement) => {
    if (recaptchaWindow) {
      observer = new MutationObserver(() => {
        if (
          recaptchaWindow.style.visibility !== 'visible' ||
          recaptchaWindow.style.opacity !== '1'
        ) {
          recaptchaWindow.style.opacity = '1';
          recaptchaWindow.style.visibility = 'visible';
        }
      });

      observer.observe(recaptchaWindow, {
        attributeFilter: ['style'],
      });
    }
  };

  const handleVerifyPhoneNumber = async (
    currentUser: User,
    phoneNumber: string,
    recaptchaContainer: string,
  ) => {
    if (recaptchaVerifierInstance.current === null) {
      removeRecaptchaWindow();

      recaptchaVerifierInstance.current = new RecaptchaVerifier(
        firebaseAuth,
        recaptchaContainer,
        {
          size: 'invisible',
          callback: () => {
            setIsCaptchaResolved(true);
          },
          'expired-callback': () => {
            setIsCaptchaResolved(false);
          },
        },
      );

      recaptchaVerifierInstance.current
        .render()
        .then(() => {
          setIsCaptchaLoaded(true);
        })
        .catch((error) => console.error('reCAPTCHA render error:', error));
    }

    try {
      const multiFactorSession = await multiFactor(currentUser).getSession();

      const phoneInfoOptions = {
        phoneNumber: phoneNumber,
        session: multiFactorSession,
      };

      const phoneAuthProvider = new PhoneAuthProvider(firebaseAuth);

      const verificationIdPromise = phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifierInstance.current,
      );

      initObserver();

      const verificationId = await verificationIdPromise;

      return verificationId;
    } catch (e) {
      errorCallback && errorCallback(e);
      throw new Error((e as Error).message);
    }
  };

  const handleVerifyPhoneNumberResolver = async (
    resolver: MultiFactorResolver,
    recaptchaContainer: string,
  ) => {
    const phoneInfoOptions = {
      multiFactorHint: resolver.hints.at(-1),
      session: resolver.session,
    };

    const phoneAuthProvider = new PhoneAuthProvider(firebaseAuth);

    if (recaptchaVerifierInstance.current === null) {
      removeRecaptchaWindow();

      recaptchaVerifierInstance.current = new RecaptchaVerifier(
        firebaseAuth,
        recaptchaContainer,
        {
          size: 'invisible',
          callback: () => {
            setIsCaptchaResolved(true);
          },
          'expired-callback': () => {
            setIsCaptchaResolved(false);
          },
        },
      );

      recaptchaVerifierInstance.current
        .render()
        .then(() => {
          setIsCaptchaLoaded(true);
        })
        .catch((error) => console.error('reCAPTCHA render error:', error));
    }

    const verificationIdPromise = phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      recaptchaVerifierInstance.current,
    );

    initObserver();

    const verificationId = await verificationIdPromise;

    return verificationId;
  };

  const handleSendVerificationCodeResolver = async (
    recaptchaContainer: string,
  ) => {
    try {
      if (!resolver) {
        signOutUser();
        navigate(AUTH_LOGIN, { state: { mfaInfoNotFound: true } });
      }
      if (
        resolver &&
        resolver.hints.length &&
        resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID
      ) {
        const verificationId = await handleVerifyPhoneNumberResolver(
          resolver,
          recaptchaContainer,
        );
        setVerificationId(verificationId);
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === FIREBASE_ERROR_CODES.AUTH_TOO_MANY_REQUESTS) {
          storageBlockTime.setData(BLOCKED_LOGIN_EXPIRY_TIME);
        }
        setErrorMsg(getFirebaseLocalizedMsg(error, locale));
        throw new Error(getFirebaseLocalizedMsg(error, locale));
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
        throw new Error(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
        throw new Error(locale?.unexpectedError);
      }
    }
  };

  const handleSendVerificationCode = async (
    phoneNumber: string,
    recaptchaContainer: string,
  ): Promise<void> => {
    try {
      const currentUser = firebaseAuth.currentUser;

      if (!currentUser) {
        setErrorMsg(locale?.requiresRecentLogin);
        return;
      }

      const verificationId = await handleVerifyPhoneNumber(
        currentUser,
        phoneNumber,
        recaptchaContainer,
      );

      setVerificationId(verificationId);
    } catch (error: unknown) {
      errorCallback && errorCallback(error);
      if (error instanceof FirebaseError) {
        if (error.code === FIREBASE_ERROR_CODES.AUTH_TOO_MANY_REQUESTS) {
          storageBlockTime.setData(BLOCKED_LOGIN_EXPIRY_TIME);
        }

        setErrorMsg(getFirebaseLocalizedMsg(error, locale));
        throw new Error(getFirebaseLocalizedMsg(error, locale));
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
        throw new Error(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
        throw new Error(locale?.unexpectedError);
      }

      throw new Error(`Something went wrong verifying phone number: ${error}`);
    }
  };

  const unenrollExistingPhoneFactors = async (user: User) => {
    const mfa = multiFactor(user);

    const phoneFactors = mfa.enrolledFactors.filter(
      (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    );

    for (const factor of phoneFactors) {
      await mfa.unenroll(factor.uid);
    }
  };

  const multifactorEnroll = async (
    currentUser: User,
    verificationId: string,
    verificationCode: string,
  ) => {
    const credential = PhoneAuthProvider.credential(
      verificationId,
      verificationCode,
    );

    await unenrollExistingPhoneFactors(currentUser);

    const multiFactorAssertion =
      PhoneMultiFactorGenerator.assertion(credential);

    await multiFactor(currentUser).enroll(multiFactorAssertion);
    await currentUser.reload();
  };

  const handleVerifyCode = async (verificationCode: string): Promise<void> => {
    try {
      if (verificationId) {
        const currentUser = firebaseAuth.currentUser;
        if (!currentUser) {
          return signOutUser();
        }

        await multifactorEnroll(currentUser, verificationId, verificationCode);

        setVerified(true);
        setErrorMsg(undefined);
      } else {
        setErrorMsg(locale?.verificationIDFail);
        throw new Error(locale?.verificationIDFail);
      }
    } catch (error) {
      console.error(`Something went wrong verifying MFA code: ${error}`);
      if (error instanceof FirebaseError) {
        setErrorMsg(
          getFirebaseLocalizedMsg(error, locale, locale?.unexpectedError),
        );
        throw new Error(getFirebaseLocalizedMsg(error, locale));
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
        throw new Error(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
        throw new Error(locale?.unexpectedError);
      }
      throw new Error(`Something went wrong verifying MFA code: ${error}`);
    }
  };

  const handleResolveSignIn = async (
    resolver: MultiFactorResolver,
    verificationCode: string,
    verificationId: string,
  ) => {
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    const response = await resolver.resolveSignIn(multiFactorAssertion);
    return response;
  };

  const handleVerifyCodeResolver = async (
    verificationCode: string,
    deleteAccount = false,
  ): Promise<void | UserCredential> => {
    try {
      if (resolver && verificationId) {
        const response = await handleResolveSignIn(
          resolver,
          verificationCode,
          verificationId,
        );
        if (deleteAccount) {
          const appleCredential = OAuthProvider.credentialFromResult(response);

          if (appleCredential) {
            const token = appleCredential?.accessToken;
            const user = firebaseAuth.currentUser;
            const response = await accountDeletionMutation({
              variables: {
                token: token ?? '',
                userId: user?.uid ?? '',
              },
            });
            if (!response.data) {
              setErrorMsg(locale?.unexpectedError);
              throw new Error('Failed to delete user');
            }
            signOutUser();
            navigate(AUTH_ACCOUNT_DELETED);
          }
        }

        await firebaseAuth.currentUser?.reload();

        return response;
      } else {
        if (!verificationId) {
          setErrorMsg(locale?.verificationIDFail);
          return;
        }
        if (!resolver) {
          return navigate(LOGOUT, { state: {} });
        }
      }
    } catch (error) {
      console.error(`Something went wrong veryfing MFA code ${error}`);
      if (error instanceof FirebaseError) {
        setErrorMsg(
          getFirebaseLocalizedMsg(error, locale, locale?.unexpectedError),
        );
        throw new Error(
          getFirebaseLocalizedMsg(error, locale, locale?.unexpectedError),
        );
      } else if (error instanceof Error) {
        setErrorMsg(error.message);
        throw new Error(error.message);
      } else {
        setErrorMsg(locale?.unexpectedError);
        throw new Error(locale?.unexpectedError);
      }
    }
  };

  useEffect(() => {
    if (errorMsg === locale?.requiresRecentLogin) {
      signOutUser();
      navigate(AUTH_MFA_REQUIRES_RECENT_LOGIN);
    } else if (errorMsg === locale?.tooManyRequests) {
      storageBlockTime.setData(BLOCKED_LOGIN_EXPIRY_TIME);
      signOutUser();
      navigate(`${AUTH_LOGIN}?mfaTooManyRequests=true`);
    }
  }, [errorMsg, signOutUser, navigate, locale]);

  return {
    isVerified,
    verificationId,
    setVerified,
    isCaptchaResolved,
    isCaptchaLoaded,
    handleSendVerificationCode,
    handleSendVerificationCodeResolver,
    handleVerifyCode,
    handleVerifyCodeResolver,
    handleVerifyPhoneNumber,
    handleVerifyPhoneNumberResolver,
    handleResolveSignIn,
    multifactorEnroll,
    errorMsg,
    setErrorMsg,
  };
};

export const useMFA = (): CheckMFA => {
  const auth = getAuth();
  const getEnrolledFactors = (): MultiFactorInfo[] => {
    try {
      const user = auth.currentUser;
      if (user) {
        return multiFactor(user).enrolledFactors;
      }
      return [];
    } catch (error) {
      console.error('Error checking MFA factors', error);
      return [];
    }
  };
  return { getEnrolledFactors };
};

export const useUserProviders = () => {
  const [userProviders, setUserProviders] = useState<string[] | null>([]);
  const [error, setError] = useState<Error | null>(null);
  const firebaseAuth = getFirebaseAuth();

  useEffect(() => {
    const fetchUserProviders = async () => {
      try {
        const user: User | null = firebaseAuth.currentUser;
        if (user) {
          const providerData = user.providerData;
          if (providerData.length > 0) {
            const providers = providerData.map((provider) => {
              return provider.providerId;
            });
            setUserProviders(providers);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error);
        }
      }
    };

    fetchUserProviders();
  }, [firebaseAuth]);
  return { userProviders, error };
};

export const useAccountDeletion = () => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });

  const navigate = useNavigate();
  const { logout } = useAuth();
  const { setMfaResolver } = useContext(AuthContext);
  const firebaseAuth = getFirebaseAuth();
  const firebaseAppleAuthProvider = getFirebaseAppleAuthProvider();

  const [accountDeletionMutation, { error: accountDeletionError }] =
    useAccountDeletionMutation({
      onCompleted: () => {
        logout();
        navigate(AUTH_ACCOUNT_DELETED);
      },
    });
  const [errorMsg, setErrorMsg] = useState<string>('');
  const accountDeletion = async () => {
    try {
      const user: User | null = firebaseAuth.currentUser;
      if (!user) {
        throw new Error('No user available');
      }

      const providerData = user.providerData;
      const providers = providerData.map((provider) => {
        return provider.providerId;
      });

      const token = await user.getIdToken();
      if (providers.includes('apple.com')) {
        await reauthenticateWithPopup(user, firebaseAppleAuthProvider);
      }

      const response = await accountDeletionMutation({
        variables: {
          token,
          userId: user.uid,
        },
      });
      if (!response.data) {
        setErrorMsg(locale?.unexpectedError);
        throw new Error('Failed to delete user');
      }
      logout();
      return true;
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        if (error.code === FIREBASE_ERROR_CODES.AUTH_USER_MISMATCH) {
          setErrorMsg(locale?.invalidEmail);
        }
        if (
          error.code === FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED
        ) {
          const resolver = getMultiFactorResolver(
            firebaseAuth,
            error as MultiFactorError,
          );

          setMfaResolver(resolver);
          navigate(MY_ACCOUNT_MFA_ACCOUNT_DELETION, {
            state: { deleteAccount: true },
          });
        }
      } else {
        setErrorMsg(locale?.unexpectedError);
      }
    }
    if (accountDeletionError) {
      setErrorMsg(accountDeletionError.message);
    }
  };
  return { accountDeletion, errorMsg };
};
